import { useState } from 'react'
import { LogIn, X, Plus, Trash2 } from 'lucide-react'
import type { GoalItem } from '@/api/attendance'

/** Check-in modal: optional explicit time (needs approval), carryover goals
 * from previous days, and today's new goals. Extracted from the "My
 * Attendance" page so it can also be mounted from the HR dashboard. */
export default function CheckinModal({
  carryoverGoals,
  onSubmit,
  onClose,
  isPending,
}: {
  carryoverGoals: GoalItem[]
  onSubmit: (carryoverIds: string[], newGoals: string[], atTime?: string, reason?: string) => void
  onClose: () => void
  isPending: boolean
}) {
  const [selectedCarryover, setSelectedCarryover] = useState<Set<string>>(new Set())
  const [newGoals, setNewGoals] = useState<string[]>([''])
  const [atTime, setAtTime] = useState('')
  const [reason, setReason] = useState('')

  const toggleCarryover = (id: string) => {
    const next = new Set(selectedCarryover)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    setSelectedCarryover(next)
  }

  const addGoalRow = () => setNewGoals([...newGoals, ''])
  const removeGoalRow = (i: number) => setNewGoals(newGoals.filter((_, idx) => idx !== i))
  const updateGoal = (i: number, val: string) => {
    const copy = [...newGoals]
    copy[i] = val
    setNewGoals(copy)
  }

  const handleSubmit = () => {
    const goals = newGoals.map((g) => g.trim()).filter(Boolean)
    onSubmit(Array.from(selectedCarryover), goals, atTime || undefined, reason.trim() || undefined)
  }

  const hasAnyGoal = selectedCarryover.size > 0 || newGoals.some((g) => g.trim())

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[80vh] flex flex-col">
        <div className="flex items-center justify-between p-5 border-b">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Check In</h2>
            <p className="text-sm text-gray-500">Set your goals for today</p>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded"><X className="h-5 w-5 text-gray-500" /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {/* Optional check-in time */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Check-in time <span className="font-normal text-gray-400">(optional)</span></label>
            <input
              type="time"
              value={atTime}
              onChange={(e) => setAtTime(e.target.value)}
              className="w-full sm:w-40 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#EC5B2C]/40 focus:border-[#EC5B2C]"
            />
            <p className="text-xs text-gray-400 mt-1">Leave blank to check in now.</p>
            {atTime && (
              <div className="mt-3 rounded-lg bg-amber-50 border border-amber-200 p-3">
                <p className="text-xs text-amber-700">
                  A check-in at a specific time needs an admin/manager's approval before it's logged.
                </p>
                <input
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Reason (optional)"
                  className="mt-2 w-full px-3 py-2 border border-amber-200 rounded-lg text-sm focus:ring-2 focus:ring-[#EC5B2C]/40 focus:border-[#EC5B2C]"
                />
              </div>
            )}
          </div>

          {/* Carryover goals */}
          {carryoverGoals.length > 0 && (
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-2">Carry over from previous days</h3>
              <div className="space-y-2">
                {carryoverGoals.map((g) => (
                  <label key={g.id} className="flex items-start gap-3 p-3 rounded-lg border border-gray-200 hover:bg-gray-50 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedCarryover.has(g.id)}
                      onChange={() => toggleCarryover(g.id)}
                      className="mt-0.5 h-4 w-4 rounded border-gray-300 text-brand focus:ring-brand"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-900">{g.description}</p>
                      <p className="text-xs text-gray-400">from {g.date}</p>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* New goals */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-medium text-gray-700">Today's goals</h3>
              <button onClick={addGoalRow} className="flex items-center gap-1 text-xs text-brand hover:text-brand-dark">
                <Plus className="h-3 w-3" /> Add
              </button>
            </div>
            <div className="space-y-2">
              {newGoals.map((goal, i) => (
                <div key={i} className="flex items-center gap-2">
                  <input
                    type="text"
                    value={goal}
                    onChange={(e) => updateGoal(i, e.target.value)}
                    placeholder={`Goal ${i + 1}`}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-brand focus:border-brand"
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addGoalRow() } }}
                  />
                  {newGoals.length > 1 && (
                    <button onClick={() => removeGoalRow(i)} className="p-1.5 text-gray-400 hover:text-red-500">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="p-5 border-t flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50">
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={isPending || !hasAnyGoal}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-[#EC5B2C] rounded-lg hover:bg-[#d54e22] disabled:opacity-50"
          >
            <LogIn className="h-4 w-4" />
            {isPending ? 'Checking in...' : 'Check In'}
          </button>
        </div>
      </div>
    </div>
  )
}
